import { useEffect, useState } from "react";
import axios from "axios";
import SEO from "../../lib/seo";
import MarketingDashboard from "./StaffMarketing";
import { Eyebrow, SectionShell } from "../../components/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "trials", label: "Trial Bookings" },
  { id: "privates", label: "Private Lesson Leads" },
  { id: "newsletter", label: "Newsletter Signups" },
  { id: "reports", label: "CRM & Reports" },
  { id: "marketing", label: "Marketing Dashboard" },
];

const SYNC_LABEL = {
  synced: ["Synced", "bg-black text-white"],
  synced_existing: ["Synced (existing)", "bg-black text-white"],
  pending: ["Syncing…", "border border-black/30 text-[#5A6270]"],
  retryable: ["Retry pending", "bg-amber-100 text-amber-900"],
  dead_letter: ["Failed", "bg-red-100 text-red-900"],
  awaiting_config: ["Awaiting CRM config", "border border-black/30 text-[#5A6270]"],
};

const SyncChip = ({ status }) => {
  if (!status) return <span className="font-mono text-[10px] text-[#9AA1AC]">local only</span>;
  const [label, cls] = SYNC_LABEL[status] || [status, "border border-black/30 text-[#5A6270]"];
  return <span className={`inline-block font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 ${cls}`}>{label}</span>;
};

const ReportsTab = ({ token }) => {
  const [report, setReport] = useState(null);
  const [gs, setGs] = useState(null);
  const [busyAction, setBusyAction] = useState("");
  const [msg, setMsg] = useState("");
  const headers = { Authorization: `Bearer ${token}` };
  const load = () => {
    axios.get(`${API}/gymsales/report`, { headers }).then((r) => setReport(r.data)).catch(() => {});
    axios.get(`${API}/gymsales/status`, { headers }).then((r) => setGs(r.data)).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (path, label) => {
    setBusyAction(label);
    setMsg("");
    try {
      const { data } = await axios.post(`${API}/gymsales/${path}`, null, { headers });
      setMsg(path === "retry" ? `Retried ${data.retried} lead(s).` : `Updated ${data.updated} lead(s) from GymSales.`);
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Action failed.");
    }
    setBusyAction("");
  };
  const configured = gs?.configured?.credentials && (gs?.configured?.company_richardson || gs?.configured?.company_north_dallas);
  const CountBlock = ({ title, obj }) => obj && Object.keys(obj).length > 0 && (
    <div className="border border-black/10 p-4">
      <p className="font-mono text-[11px] uppercase tracking-widest text-[#5A6270]">{title}</p>
      <div className="mt-2 space-y-1 text-sm">
        {Object.entries(obj).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4"><span className="capitalize">{k.replace(/-/g, " ")}</span><span className="font-bold">{v}</span></div>
        ))}
      </div>
    </div>
  );
  return (
    <div data-testid="staff-reports-tab">
      <div className="border border-black/10 p-4 flex flex-wrap items-center gap-4" data-testid="gymsales-config-banner">
        <div>
          <p className="font-display font-black uppercase">ABC GymSales CRM</p>
          <p className="text-sm text-[#5A6270]">
            {configured
              ? "Connected — new leads sync automatically."
              : "Awaiting configuration — leads are stored locally and will sync once GymSales credentials & company IDs are added to the server environment."}
          </p>
          {gs && (
            <p className="mt-1 font-mono text-[11px] text-[#5A6270]">
              credentials: {gs.configured.credentials ? "set" : "missing"} · richardson id: {gs.configured.company_richardson ? "set" : "missing"} · north dallas id: {gs.configured.company_north_dallas ? "set" : "missing"} · external_id: {gs.configured.external_id_enabled ? "enabled" : "off"}
            </p>
          )}
        </div>
        <div className="flex gap-2 ml-auto">
          <button onClick={() => act("retry", "retry")} disabled={!!busyAction} data-testid="gymsales-retry-btn"
            className="border border-[#0A0B0D] font-display font-bold uppercase text-sm px-4 py-2 hover:bg-[#0A0B0D] hover:text-white transition-colors disabled:opacity-50">
            {busyAction === "retry" ? "Retrying…" : "Retry Sync"}
          </button>
          <button onClick={() => act("status-sync", "status")} disabled={!!busyAction || !configured} data-testid="gymsales-status-sync-btn"
            className="border border-[#0A0B0D] font-display font-bold uppercase text-sm px-4 py-2 hover:bg-[#0A0B0D] hover:text-white transition-colors disabled:opacity-50">
            {busyAction === "status" ? "Syncing…" : "Pull Lead Statuses"}
          </button>
        </div>
      </div>
      {msg && <p className="mt-3 text-sm font-mono" data-testid="gymsales-action-msg">{msg}</p>}
      {report && (
        <>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <div className="border border-black/10 p-4"><p className="font-mono text-[11px] uppercase tracking-widest text-[#5A6270]">Leads · {report.window_days}d</p><p className="mt-1 font-display font-black text-3xl">{report.total_leads}</p></div>
            {report.trial_bookings != null && <div className="border border-black/10 p-4"><p className="font-mono text-[11px] uppercase tracking-widest text-[#5A6270]">Trial bookings</p><p className="mt-1 font-display font-black text-3xl">{report.trial_bookings}</p>{report.lead_to_trial_rate != null && <p className="text-xs text-[#5A6270]">{Math.round(report.lead_to_trial_rate * 100)}% of leads</p>}</div>}
            {report.sales != null && <div className="border border-black/10 p-4"><p className="font-mono text-[11px] uppercase tracking-widest text-[#5A6270]">Sales</p><p className="mt-1 font-display font-black text-3xl">{report.sales}</p>{report.lead_to_sale_rate != null && <p className="text-xs text-[#5A6270]">{Math.round(report.lead_to_sale_rate * 100)}% of leads</p>}</div>}
            {report.show_rate != null && <div className="border border-black/10 p-4"><p className="font-mono text-[11px] uppercase tracking-widest text-[#5A6270]">Show rate</p><p className="mt-1 font-display font-black text-3xl">{Math.round(report.show_rate * 100)}%</p></div>}
            {report.membership_value_total != null && <div className="border border-black/10 p-4"><p className="font-mono text-[11px] uppercase tracking-widest text-[#5A6270]">Membership value (ABC)</p><p className="mt-1 font-display font-black text-3xl">${report.membership_value_total.toLocaleString()}</p></div>}
          </div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <CountBlock title="By location" obj={report.by_location} />
            <CountBlock title="By program" obj={report.by_program} />
            <CountBlock title="By source" obj={report.by_source} />
            <CountBlock title="By status" obj={report.by_status} />
            {gs?.sync_counts && <CountBlock title="CRM sync state" obj={gs.sync_counts} />}
          </div>
          <p className="mt-4 text-xs text-[#5A6270] font-mono">{report.note}</p>
        </>
      )}
    </div>
  );
};

const Cell = ({ children, className = "" }) => <div className={`text-sm ${className}`}>{children}</div>;

const StaffLogin = ({ onToken }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/staff/login`, { password });
      sessionStorage.setItem("staff_token", data.token);
      onToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed — try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <SectionShell className="bg-white">
      <form onSubmit={submit} className="max-w-sm border border-black/10 p-8" data-testid="staff-login-form">
        <h2 className="font-display font-black uppercase text-2xl">Staff Access</h2>
        <p className="mt-1 text-sm text-[#5A6270]">Enter the staff password to view leads.</p>
        <label htmlFor="staff-pw" className="block text-sm font-bold mt-5 mb-1">Password</label>
        <input id="staff-pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-3 border border-black/20 focus:outline-none focus:border-[#0A0B0D]" data-testid="staff-password-input" />
        {error && <p className="mt-2 text-sm text-neutral-400" data-testid="staff-login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="staff-login-submit-btn"
          className="mt-4 w-full bg-[#111315] border border-white/20 hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide py-3 transition-colors disabled:opacity-60">
          {busy ? "Checking…" : "Log In"}
        </button>
      </form>
    </SectionShell>
  );
};

export default function StaffPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("staff_token"));
  const [leads, setLeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState("trials");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([axios.get(`${API}/leads/private-lesson`, { headers }), axios.get(`${API}/newsletter`, { headers })])
      .then(([l, n]) => { setLeads(l.data); setSubs(n.data); })
      .catch((e) => {
        if (e.response?.status === 401) {
          sessionStorage.removeItem("staff_token");
          setToken(null);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const logout = () => { sessionStorage.removeItem("staff_token"); setToken(null); };

  const trials = leads.filter((l) => l.goal.startsWith("FREE TRIAL REQUEST"));
  const privates = leads.filter((l) => !l.goal.startsWith("FREE TRIAL REQUEST"));
  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  const rows = tab === "trials" ? trials : tab === "privates" ? privates : subs;

  if (token && tab === "marketing") {
    return (
      <>
        <SEO title="Marketing Dashboard | Mezger Martial Arts Staff" description="Internal marketing dashboard." schemas={[]} noindex />
        <MarketingDashboard token={token} onBack={() => setTab("trials")} onLogout={logout} />
      </>
    );
  }

  return (
    <>
      <SEO title="Staff — Leads Dashboard | Mezger Martial Arts" description="Internal staff dashboard for trial bookings, private lesson leads, and newsletter signups." schemas={[]} noindex />
      <section className="bg-[#0A0B0D] text-white pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Eyebrow>Staff Only · Internal</Eyebrow>
              <h1 className="mt-2 font-display font-black uppercase text-4xl sm:text-5xl">Leads Dashboard</h1>
            </div>
            {token && (
              <button onClick={logout} data-testid="staff-logout-btn" className="border border-white/30 text-white/70 hover:text-white font-display font-bold uppercase tracking-wide text-sm px-4 py-2 transition-colors">
                Log Out
              </button>
            )}
          </div>
          {token && (
          <div className="mt-6 flex flex-wrap gap-2">
            {TABS.map((t) => {
              const count = t.id === "trials" ? trials.length : t.id === "privates" ? privates.length : t.id === "newsletter" ? subs.length : null;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`staff-tab-${t.id}`}
                  className={`px-4 py-2 font-display font-bold uppercase tracking-wide text-sm border transition-colors ${tab === t.id ? "bg-white text-[#0A0B0D] border-white" : "border-white/30 text-white/70 hover:text-white"}`}>
                  {t.label} {count != null && <span className="ml-1 font-mono">({count})</span>}
                </button>
              );
            })}
          </div>
          )}
        </div>
      </section>
      {!token ? (
        <StaffLogin onToken={setToken} />
      ) : (
      <SectionShell className="bg-white">
        {tab === "reports" ? (
          <ReportsTab token={token} />
        ) : loading ? (
          <p className="text-[#5A6270]" data-testid="staff-loading">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-[#5A6270]" data-testid="staff-empty">No entries yet.</p>
        ) : tab === "newsletter" ? (
          <div className="border border-black/10 divide-y divide-black/5" data-testid="staff-newsletter-list">
            <div className="grid grid-cols-3 gap-2 p-3 bg-[#F7F8F9] font-mono text-[11px] uppercase tracking-widest text-[#5A6270]">
              <span>Email</span><span>Source page</span><span>Date</span>
            </div>
            {rows.map((s) => (
              <div key={s.id} className="grid grid-cols-3 gap-2 p-3 items-center">
                <Cell className="font-bold">{s.email}</Cell>
                <Cell className="text-[#5A6270]">{s.source_page || "—"}</Cell>
                <Cell className="text-[#5A6270]">{fmt(s.created_at)}</Cell>
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-black/10 divide-y divide-black/5" data-testid={`staff-${tab}-list`}>
            <div className="hidden md:grid grid-cols-7 gap-2 p-3 bg-[#F7F8F9] font-mono text-[11px] uppercase tracking-widest text-[#5A6270]">
              <span>Name</span><span>Phone</span><span>Email</span><span>Location</span><span>Goal / Interest</span><span>Received · Source</span><span>CRM</span>
            </div>
            {rows.map((l) => (
              <div key={l.id} className="grid md:grid-cols-7 gap-2 p-3 items-center">
                <Cell className="font-bold">{l.name}</Cell>
                <Cell><a href={`tel:${l.phone}`} className="hover:text-neutral-500">{l.phone}</a></Cell>
                <Cell className="truncate"><a href={`mailto:${l.email}`} className="hover:text-neutral-500">{l.email}</a></Cell>
                <Cell className="uppercase font-mono text-xs">{l.location}</Cell>
                <Cell className="text-[#5A6270]">{l.goal.replace("FREE TRIAL REQUEST: ", "")}</Cell>
                <Cell className="text-[#5A6270] text-xs">{fmt(l.created_at)}<br /><span className="font-mono">{l.utm_source || "direct"}{l.utm_campaign ? ` · ${l.utm_campaign}` : ""}</span></Cell>
                <Cell><SyncChip status={l.gymsales_sync_status} />{l.lead_status && <span className="block font-mono text-[10px] uppercase text-[#5A6270] mt-1">{l.lead_status}</span>}</Cell>
              </div>
            ))}
          </div>
        )}
        <p className="mt-6 text-xs text-[#5A6270] font-mono">Session lasts 8 hours. Change the staff password in backend settings before rotating staff.</p>
      </SectionShell>
      )}
    </>
  );
}
